// Filtros y píldoras de las tablas POS de «Nueva venta» (Teléfono y Accesorio). Puro: las opciones
// salen de los datos de la tienda, alfabéticas; la capacidad va de menor a mayor.
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { coincide, terminosDe } from '@/domains/sales/wizard/catalogFilters';
import { SaleFinancierEnum } from '@fiado/type-kit/bin/retailWizard/enums/SaleFinancierEnum';
import { isSoldOut } from '@/domains/sales/wizard/catalogAvailability';
import { capacityGb, formatStorage } from '@/shared/lib/format/capacity';
import { promoLabelOf } from './posCatalog';

export interface PosFilters {
    query: string;
    brands: string[];
    /** Pesos tal como se teclean; `''` es sin tope. */
    minPrice: string;
    maxPrice: string;
    /** Financiera que tiene que aceptar el producto; `''` es cualquiera. */
    financier: string;
    tiers: string[];
    capacities: string[];
    promoOnly: boolean;
    inStockOnly: boolean;
}

/** «Solo con stock» arranca prendido: lo agotado no se puede vender. */
export const DEFAULT_POS_FILTERS: PosFilters = {
    query: '',
    brands: [],
    minPrice: '',
    maxPrice: '',
    financier: '',
    tiers: [],
    capacities: [],
    promoOnly: false,
    inStockOnly: true,
};

/** Cuenta lo que difiere del estado inicial, que es lo que «Limpiar» deshace. */
export function countActivePosFilters(filters: PosFilters): number {
    return [
        filters.query.trim() !== '',
        filters.brands.length > 0,
        filters.minPrice !== '',
        filters.maxPrice !== '',
        filters.financier !== '',
        filters.tiers.length > 0,
        filters.capacities.length > 0,
        filters.promoOnly,
        !filters.inStockOnly,
    ].filter(Boolean).length;
}

export function toggleValue(values: string[], value: string): string[] {
    return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function pesosToCents(raw: string, fallback: number): number {
    const pesos = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(pesos)) return fallback;
    return Math.round(pesos * 100);
}

export function hasPromo(item: CatalogItem): boolean {
    return item.hasPromo || promoLabelOf(item) !== null;
}

const SUREKEEP: string = SaleFinancierEnum.SUREKEEP;
const NO_FINANCIER: string = SaleFinancierEnum.NONE;

export function acceptsSurekeep(item: CatalogItem): boolean {
    return item.eligibleFinanciers.includes(SUREKEEP);
}

/** Si el producto lleva promo; cada pantalla puede pasar su propio criterio. */
export type IsPromoted = (item: CatalogItem) => boolean;

export function filterPosItems(
    items: readonly CatalogItem[],
    filters: PosFilters,
    isPromoted: IsPromoted = hasPromo,
): CatalogItem[] {
    const terms = terminosDe(filters.query);
    const minCents = pesosToCents(filters.minPrice, 0);
    const maxCents = pesosToCents(filters.maxPrice, Number.POSITIVE_INFINITY);
    return items.filter(
        (item) =>
            coincide(item, terms) &&
            (filters.brands.length === 0 || filters.brands.includes(item.brand)) &&
            item.cashPriceCents >= minCents &&
            item.cashPriceCents <= maxCents &&
            (filters.financier === '' || item.eligibleFinanciers.includes(filters.financier)) &&
            (filters.tiers.length === 0 || filters.tiers.includes(item.tier)) &&
            (filters.capacities.length === 0 ||
                filters.capacities.includes(formatStorage(item.capacity))) &&
            (!filters.promoOnly || isPromoted(item)) &&
            (!filters.inStockOnly || !isSoldOut(item)),
    );
}

const byText = (a: string, b: string): number => a.localeCompare(b, 'es-MX');

function distinct(values: string[]): string[] {
    return [...new Set(values.map((value) => value.trim()))].filter((value) => value !== '');
}

export function brandOptions(items: readonly CatalogItem[]): string[] {
    return distinct(items.map((item) => item.brand)).sort(byText);
}

export function tierOptions(items: readonly CatalogItem[]): string[] {
    return distinct(items.map((item) => item.tier)).sort(byText);
}

/** Financieras presentes, SureKeep primero y el resto alfabético. */
export function financierOptions(items: readonly CatalogItem[]): string[] {
    return distinct(items.flatMap((item) => item.eligibleFinanciers))
        .filter((code) => code !== NO_FINANCIER)
        .sort((a, b) => (a === SUREKEEP ? -1 : b === SUREKEEP ? 1 : byText(a, b)));
}

/** «64 GB» antes que «128 GB»: se ordena por los GB y, si no hay cifra, por el texto. */
export function capacityOptions(items: readonly CatalogItem[]): string[] {
    const size = (value: string): number => capacityGb(value) ?? 0;
    return distinct(items.map((item) => formatStorage(item.capacity))).sort(
        (a, b) => size(a) - size(b) || byText(a, b),
    );
}

export type PosStat = 'all' | 'promo' | 'surekeep' | 'last';

export function matchesPosStat(
    item: CatalogItem,
    stat: PosStat,
    isLastPieces: (item: CatalogItem) => boolean,
    isPromoted: IsPromoted = hasPromo,
): boolean {
    switch (stat) {
        case 'promo':
            return isPromoted(item);
        case 'surekeep':
            return acceptsSurekeep(item);
        case 'last':
            return isLastPieces(item);
        default:
            return true;
    }
}

export type PosStats = Record<PosStat, number>;

export function posStats(
    items: readonly CatalogItem[],
    isLastPieces: (item: CatalogItem) => boolean,
    isPromoted: IsPromoted = hasPromo,
): PosStats {
    const count = (stat: PosStat): number =>
        items.filter((item) => matchesPosStat(item, stat, isLastPieces, isPromoted)).length;
    return {
        all: items.length,
        promo: count('promo'),
        surekeep: count('surekeep'),
        last: count('last'),
    };
}
